import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type {
  IxcTradeV1FirmTradeTransaction,
  IxcTradeV1NettingIndicationTransaction,
  IxcTradeV1TradeData,
  IxcTradeV1TradeKeys,
  IxcTradeV1TradeState,
  IxcTradeV1TradeTotalsData,
} from '@interxfi/types/generated/api';
import { getNumericStringOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import type { ReplaceValuesAndMakeOptional } from '@interxfi/utilities/types/replace';
import { getAssetDetailPath } from '../asset/asset-routing';
import { getUtcTradeListItems } from '../dtcc/utc-trade/utc-trade-list-items';
import { getInventoryFirmOrderDetailPath } from '../inventory/firm-order/inventory-firm-order-routing';
import { getJ5StateMetadataListItems } from '../j5/state/j5-state-list-items';
import { getOrderTradeTransactionListItems } from '../order/order-list-items';
import { getOrderDetailPath } from '../order/order-routing';
import { OrderSidePill } from '../order/order-side-pill';
import { getTradeDetailPath } from './trade-routing';
import { TradeStatusPill } from './trade-status-pill';

export function getTradeFirmTradeTransactionListItems(data: IxcTradeV1FirmTradeTransaction | undefined, prefix = '') {
  return {
    firmOrderId: getReadonlyListItemId(
      `${prefix}firmOrderId`,
      'Firm Order ID',
      data?.firmOrderId,
      getInventoryFirmOrderDetailPath(data?.firmOrderId),
    ),
    executionId: getReadonlyListItemId(`${prefix}executionId`, 'Execution ID', data?.executionId),
    executionTime: getReadonlyListItemDate(`${prefix}executionTime`, 'Execution Time', data?.executionTime, 'timestamp_iso_ms'),
    tradeDate: getReadonlyListItemDate(`${prefix}tradeDate`, 'Trade Date', data?.tradeDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    side: getReadonlyListItemTranslatedString(`${prefix}side`, 'Side', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Side',
      value: data?.side,
    }),
    sidePill: getReadonlyListItemPill(`${prefix}sidePill`, 'Side', data?.side ? <OrderSidePill side={data.side} /> : null),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    price: getReadonlyListItemCurrency(`${prefix}price`, 'Price', data?.price),
    grossAmount: getReadonlyListItemCurrency(`${prefix}grossAmount`, 'Gross Amount', data?.grossAmount),
  } as const satisfies ReadonlyListItemRecord;
}

export function getTradeTotalsDataListItems(data: IxcTradeV1TradeTotalsData | undefined, prefix = '') {
  return {
    buyQuantity: getReadonlyListItemNumber(`${prefix}buyQuantity`, 'Buy Quantity', getNumericStringOrUndefinedFromNullable(data, 'buyQuantity')),
    sellQuantity: getReadonlyListItemNumber(`${prefix}sellQuantity`, 'Sell Quantity', getNumericStringOrUndefinedFromNullable(data, 'sellQuantity')),
    buyValue: getReadonlyListItemCurrency(`${prefix}buyValue`, 'Buy Value', getNumericStringOrUndefinedFromNullable(data, 'buyValue')),
    sellValue: getReadonlyListItemCurrency(`${prefix}sellValue`, 'Sell Value', getNumericStringOrUndefinedFromNullable(data, 'sellValue')),
  } as const satisfies ReadonlyListItemRecord;
}

export function getTradeDataListItems(data: IxcTradeV1TradeData | undefined, prefix = '') {
  return {
    orderId: getReadonlyListItemId(`${prefix}orderId`, 'Order ID', data?.orderId, getOrderDetailPath(data?.orderId)),
    utc: data?.utc ? getUtcTradeListItems(data.utc, `${prefix}utc.`) : undefined,
    route: data?.route ? getOrderTradeTransactionListItems(data.route, `${prefix}route.`) : undefined,
    firmRoute: data?.firmRoute ? getTradeFirmTradeTransactionListItems(data.firmRoute, `${prefix}firmRoute.`) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getTradeStateListItems(
  data: IxcTradeV1TradeState | undefined,
  linkContext: ReplaceValuesAndMakeOptional<IxcTradeV1TradeKeys, string, boolean> = {
    tradeId: false,
  },
) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    tradeId: getReadonlyListItemId('tradeId', 'Trade ID', data?.tradeId, linkContext.tradeId ? getTradeDetailPath(data?.tradeId) : undefined),
    executionId: getReadonlyListItemId('executionId', 'Execution ID', data?.executionId),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcTradeV1',
      enumName: 'IxcTradeV1TradeStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <TradeStatusPill status={data.status} /> : null),
    data: getTradeDataListItems(data?.data, 'data'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getTradeNettingIndicationTransactionListItems(data: IxcTradeV1NettingIndicationTransaction | undefined, prefix = '') {
  return {
    orderId: getReadonlyListItemId(`${prefix}orderId`, 'Order ID', data?.orderId, getOrderDetailPath(data?.orderId)),
    executionId: getReadonlyListItemId(`${prefix}executionId`, 'Execution ID', data?.executionId),
    executionTime: getReadonlyListItemDate(`${prefix}executionTime`, 'Execution Time', data?.executionTime, 'timestamp_iso_ms'),
    tradeDate: getReadonlyListItemDate(`${prefix}tradeDate`, 'Trade Date', data?.tradeDate, 'date_iso'),
    settlementDate: getReadonlyListItemDate(`${prefix}settlementDate`, 'Settlement Date', data?.settlementDate, 'date_iso'),
    symbol: getReadonlyListItemString(`${prefix}symbol`, 'Symbol', data?.symbol),
    cusip: getReadonlyListItemString(`${prefix}cusip`, 'CUSIP', data?.cusip),
    assetId: getReadonlyListItemId(`${prefix}assetId`, 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    side: getReadonlyListItemTranslatedString(`${prefix}side`, 'Side', {
      namespace: 'ixcOrdersV1',
      enumName: 'IxcOrdersV1Side',
      value: data?.side,
    }),
    sidePill: getReadonlyListItemPill(`${prefix}sidePill`, 'Side', data?.side ? <OrderSidePill side={data.side} /> : null),
    quantity: getReadonlyListItemNumber(`${prefix}quantity`, 'Quantity', data?.quantity),
    fillPrice: getReadonlyListItemCurrency(`${prefix}fillPrice`, 'Fill Price', data?.fillPrice),
    grossAmount: getReadonlyListItemCurrency(`${prefix}grossAmount`, 'Gross Amount', data?.grossAmount),
    netSettlementAmount: getReadonlyListItemCurrency(`${prefix}netSettlementAmount`, 'Net Settlement Amount', data?.netSettlementAmount),
    nettingIndicator: getReadonlyListItemTranslatedString(`${prefix}nettingIndicator`, 'Netting Indicator', {
      namespace: 'excDtccUtcV1',
      enumName: 'ExcDtccUtcV1NettingIndicator',
      value: data?.nettingIndicator,
    }),
  } as const satisfies ReadonlyListItemRecord;
}
